import type { ReactNode } from "react";
import { MonitorFill as Monitor, ArrowLeftFill as ArrowLeft } from "@/components/icons/solid-extra-icons";

import { useIsMobile } from "@/hooks/use-media-query";
import { isNative } from "@/lib/platform";

/**
 * Gate heavy desktop-only modules behind a "Open on Desktop" wall when the
 * user is on a phone-sized viewport.
 *
 * The targeted modules (metaverse, science lab, school onboarding,
 * neural network playground) all share these properties:
 *
 *   • They depend on Three.js / WebGL drag-drop / multi-pane builders that
 *     simply do not fit a 360px viewport — squeezing them down would be
 *     dishonest and brittle.
 *   • The information density and the input model (mouse, keyboard) are
 *     fundamentally desktop-class.
 *   • They are rarely the daily-driver for parents/students on phones.
 *
 * Rather than partially render a broken UI on mobile we redirect the user
 * to a desktop in a single, friendly, branded panel. The component falls
 * through to its children on tablets and laptops so authors don't need to
 * duplicate any logic.
 *
 * Usage:
 *
 *   <DesktopOnly title="Metaverse Campus" description="…explain why…">
 *     <MetaverseCanvas />
 *   </DesktopOnly>
 *
 * The decision is based on `useIsMobile()` which currently means
 * `(max-width: 767.98px)` — tablets get the real experience.
 */
export function DesktopOnly({
  title,
  description,
  children,
  native = false,
}: {
  title: string;
  description: string;
  children: ReactNode;
  /**
   * When true, also show the "Open on Desktop" wall inside the native app
   * regardless of viewport size. Use for flows that must be completed on a
   * desktop even though they'd technically fit a phone screen — e.g. School
   * Setup, which we deliberately keep off phones.
   */
  native?: boolean;
}) {
  const isMobile = useIsMobile();
  const blocked = isMobile || (native && isNative());
  if (!blocked) return <>{children}</>;

  return (
    <div className="min-h-[60dvh] flex items-center justify-center p-6">
      <div
        className="panel rounded-2xl max-w-sm w-full text-center p-6 space-y-4"
      >
        <div
          className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center"
          style={{ background: "var(--color-brand-bg)", color: "var(--color-brand)" }}
        >
          <Monitor size={26} />
        </div>
        <div className="space-y-1.5">
          <h2 className="text-lg font-bold" style={{ color: "var(--color-text-1)" }}>{title}</h2>
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-3)" }}>
            {description}
          </p>
        </div>
        <p className="text-xs" style={{ color: "var(--color-text-4)" }}>
          Open this page on a desktop or larger tablet to use the full experience.
        </p>
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center justify-center gap-1.5 min-h-touch px-4 rounded-xl text-sm font-semibold transition-colors"
          style={{
            background: "var(--color-surface-hover)",
            color: "var(--color-text-2)",
            border: "1px solid var(--color-border)",
          }}
        >
          <ArrowLeft size={14} /> Back
        </button>
      </div>
    </div>
  );
}
